/**
 * The caller's own profile.
 *
 * Ported from the app's `profile_screen.dart`, `student_edit_profile_screen`,
 * `mentor_profile_screen`, `mentor_edit_profile_screen` and
 * `change_password_screen` — five screens there, one page here, because a
 * portal has room to show and edit in the same place.
 *
 * *** TWO ROLES, TWO ENDPOINTS, ONE ROUTE ***
 * A student has a verified email and a category; a mentor has a bio and a
 * visibility rule. `/me/profile` and `/mentor/profile` answer different
 * questions, and merging them would produce a type that is mostly nulls.
 *
 * An ADMIN has neither — their account is managed from Admin accounts — so
 * this page tells them that rather than calling an endpoint that 403s.
 */

import { PageHeader, PageStack, Card } from '@/components/ui/Page'
import { useAuth } from '@/lib/auth'
import { PolicyLinksGroup } from '@/components/ui/PolicyLinks'
import { CategoryPicker } from './CategoryPicker'
import { StudentProfile } from './StudentProfile'
import { MentorProfile } from './MentorProfile'
import styles from './ProfilePage.module.css'

export function ProfilePage() {
  const { user } = useAuth()
  const isMentor = user?.role === 'MENTOR'
  const isStudent = user?.role === 'STUDENT'

  return (
    <PageStack>
      <PageHeader
        title="Your profile"
        description={
          isMentor
            ? 'What students see, and how you sign in.'
            : 'Your details and how we reach you.'
        }
      />

      {/*
        *** THE SUBJECT AREA COMES FIRST ***
        Without one a student cannot see a mentor or book anything, and no
        other screen on the portal lets them set it. It outranks their own
        name.
      */}
      {isStudent && <CategoryPicker />}
      {isStudent && <StudentProfile />}
      {isMentor && <MentorProfile />}

      {!isStudent && !isMentor && (
        <Card>
          <p className={styles.muted}>
            Administrator accounts are managed from Admin accounts. Ask
            whoever set yours up if something needs changing.
          </p>
        </Card>
      )}

      {/*
        *** THE APP PUTS THESE IN ITS SETTINGS SCREEN TOO ***
        `policy_links.dart` renders them on both sign-in screens AND both
        profile screens. The sidebar carries them for every page; this is
        the place someone goes looking for them.
      */}
      <PolicyLinksGroup slug={user?.tenant_slug} />
    </PageStack>
  )
}
